/**
 * The diagnostic's analytics, in the two places it runs.
 *
 * Standalone, the page owns its own measurement: it injects the GA4 loader and
 * (when tracking is on) the HubSpot script, and `track` calls `gtag` directly.
 *
 * Embedded, it owns nothing. The landing page already has GA4 and HubSpot on
 * it, and a second copy inside the frame would double-count every pageview and
 * fight over the `hutk` cookie the gate reads. So the frame posts each event to
 * its parent and `public/embed.js` forwards it to the landing page's `gtag` or
 * `dataLayer`. No script is ever loaded inside a frame.
 *
 * Which events exist, and with which params, is the table in
 * `handoff/hard-wired-diagnostic/integrations.md`.
 */

import { logger } from "@/lib/logger";

import { isEmbedded } from "./embed";

/** The postMessage type the embed loader listens for. */
export const EVENT_MESSAGE = "hw:event";

/** Where the names that have already fired are remembered, per tab. */
export const ANALYTICS_STORAGE_KEY = "hw-analytics-v1";

export const EVENT_START = "hw_diag_start";
export const EVENT_ANSWER = "hw_diag_answer";
export const EVENT_COMPLETE = "hw_diag_complete";
export const EVENT_LEAD = "hw_diag_lead";
export const EVENT_DOWNLOAD = "hw_diag_download";

/** What a param may carry: never anything personal (SPEC section 7). */
export type AnalyticsParams = Record<string, string | number | boolean>;

/** "An event happened." Posted to the parent page when embedded. */
export interface EventMessage {
  type: typeof EVENT_MESSAGE;
  name: string;
  params: AnalyticsParams;
}

/**
 * The events that describe a visit rather than an interaction: each one is a
 * milestone that can only be passed once, so a refresh, a restored session or
 * a second render must not report it twice.
 */
const ONE_SHOT: readonly string[] = [EVENT_START, EVENT_COMPLETE, EVENT_LEAD, EVENT_DOWNLOAD];

type GtagFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: GtagFunction;
    dataLayer?: unknown[];
  }
}

// ─── the one-shot guard ──────────────────────────────────────────────────

/**
 * The key under which an event counts as "already fired", or null when the
 * event may fire as often as it happens.
 *
 * The six answers are one event each, so they are keyed by question: answering
 * question three twice (a refresh mid-flight, a retried send) is still one
 * `hw_diag_answer` for that question.
 */
export function firedKey(name: string, params?: AnalyticsParams): string | null {
  if (ONE_SHOT.includes(name)) {
    return name;
  }
  if (name === EVENT_ANSWER) {
    const qid = params?.qid;
    return typeof qid === "string" && qid ? `${name}:${qid}` : name;
  }
  return null;
}

function readFired(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(ANALYTICS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((item): item is string => typeof item === "string"));
  } catch {
    // Storage that cannot be read is storage that cannot dedupe. Reporting an
    // event twice is better than losing the funnel entirely.
    return new Set();
  }
}

function writeFired(fired: Set<string>): void {
  try {
    window.sessionStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify([...fired]));
  } catch (error) {
    logger.warn("[analytics]", "could not remember which events have fired", error);
  }
}

/** Forget every fired event. Used when the visitor starts over. */
export function resetFiredEvents(): void {
  try {
    window.sessionStorage.removeItem(ANALYTICS_STORAGE_KEY);
  } catch (error) {
    logger.warn("[analytics]", "could not clear the fired events", error);
  }
}

// ─── sending ─────────────────────────────────────────────────────────────

export function buildEventMessage(name: string, params?: AnalyticsParams): EventMessage {
  return { type: EVENT_MESSAGE, name, params: params ?? {} };
}

/**
 * Hand one event to whatever is listening.
 *
 * Embedded that is the parent page, over postMessage with `*`: the parent is
 * not known at build time, and an event name with a question id and a score
 * band carries nothing another listener could misuse.
 */
function dispatch(name: string, params?: AnalyticsParams): void {
  if (typeof window === "undefined") {
    return;
  }
  const values = params ?? {};

  if (isEmbedded()) {
    try {
      window.parent?.postMessage(buildEventMessage(name, values), "*");
    } catch (error) {
      logger.warn("[analytics]", "could not reach the parent page", error);
    }
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", name, values);
    return;
  }
  if (window.dataLayer && typeof window.dataLayer.push === "function") {
    window.dataLayer.push({ event: name, ...values });
    return;
  }
  logger.debug("[analytics]", name, values);
}

/**
 * Report one event, at most as often as it is allowed to happen.
 *
 * Safe to call from a render path: an event that has already fired is dropped
 * here rather than at the call site.
 */
export function track(name: string, params?: AnalyticsParams): void {
  if (typeof window === "undefined") {
    return;
  }
  const key = firedKey(name, params);
  if (key) {
    const fired = readFired();
    if (fired.has(key)) {
      logger.debug("[analytics]", "already fired", key);
      return;
    }
    fired.add(key);
    writeFired(fired);
  }
  logger.debug("[analytics]", name, params ?? {});
  dispatch(name, params);
}

// ─── booting the standalone scripts ──────────────────────────────────────

export const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";
export const HUBSPOT_SRC = "https://js.hs-scripts.com";

/** The HubSpot loader's id, which is also how a second injection is avoided. */
export const HUBSPOT_SCRIPT_ID = "hs-script-loader";

export function gtagSrc(measurementId: string): string {
  return `${GTAG_SRC}?id=${encodeURIComponent(measurementId)}`;
}

export function hubspotSrc(portalId: string): string {
  return `${HUBSPOT_SRC}/${encodeURIComponent(portalId)}.js`;
}

/** The non-secret settings the browser needs, as `/diagnostic/config` serves them. */
export interface AnalyticsConfig {
  ga_measurement_id?: string | null;
  hubspot_portal_id: string;
  hubspot_tracking_enabled: boolean;
}

let booted = false;

function loadGtag(measurementId: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = gtagSrc(measurementId);
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      // GA4's own snippet pushes `arguments`, not an array, and the tag reads
      // it positionally. An array is indistinguishable to it.
      window.dataLayer?.push(args);
    };
  }
  window.gtag("js", new Date());
  // No advertising features and no full IP: the diagnostic measures a funnel,
  // not a person.
  window.gtag("config", measurementId, { anonymize_ip: true });
  logger.debug("[analytics]", "gtag loaded", measurementId);
}

function loadHubSpot(portalId: string): void {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement("script");
  script.id = HUBSPOT_SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = hubspotSrc(portalId);
  document.head.appendChild(script);
  logger.debug("[analytics]", "hubspot tracking loaded", portalId);
}

/**
 * Start measurement for this page, once.
 *
 * Does nothing inside a frame: the parent page is already measured, and the
 * `hutk` cookie the gate submits belongs to the parent's HubSpot script, not
 * to a second one in here.
 */
export function bootAnalytics(config: AnalyticsConfig): void {
  if (booted || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  booted = true;

  if (isEmbedded()) {
    logger.debug("[analytics]", "embedded: the parent page owns measurement");
    return;
  }
  if (config.ga_measurement_id) {
    loadGtag(config.ga_measurement_id);
  }
  if (config.hubspot_tracking_enabled && config.hubspot_portal_id) {
    loadHubSpot(config.hubspot_portal_id);
  }
}
